<template>
  <div class="map-container">
    <div :id="mapId" class="map-content">
      <!-- 比例尺容器 -->
      <div id="scalebar" class="scalebar-container"></div>
    </div>

    <!-- Marker点控制按钮 -->
    <MarkerControlPanel :status-visibility="statusVisibility" :status-counts="statusCounts"
      :ext-layer-data="extLayerData" :water-factory-data="waterFactoryData" @toggle-status="toggleStatusVisibility"
      @toggle-ext-layer="toggleExtLayerVisibility" @toggle-water-factory="toggleWaterFactoryVisibility" />

    <!-- 标记点信息面板 -->
    <MarkerInfoPanel :show-info-panel="showInfoPanel" :marker-info="markerInfo" @close-panel="closeInfoPanel" />

    <!-- 报警面板 -->
    <AlertPanel />
  </div>
</template>

<script>
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import * as olProj from 'ol/proj';
import { getUUID } from '@/utils/util';
import { LonLat } from '@/store/mutation-types';
import storage from 'store';
import { defaults as defaultInteractions } from 'ol/interaction'
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Style, Icon, Text } from 'ol/style';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import { fromLonLat } from 'ol/proj';
import { Fill, Stroke } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import Overlay from 'ol/Overlay';
import ScaleLine from 'ol/control/ScaleLine';
import MarkerControlPanel from './MarkerControlPanel.vue';
import MarkerInfoPanel from './MarkerInfoPanel.vue';
import AlertPanel from './AlertPanel.vue';

export default {
  components: {
    MarkerControlPanel,
    MarkerInfoPanel,
    AlertPanel
  },
  data () {
    return {
      markerUrl: '/pipe-network/piezometer/analysis/gisData',
      extLayerUrl: '/pipe-network/zone-metering/water-supply-range',
      extLayerData: [],
      markerData: [],
      waterFactoryData: [],
      mapId: getUUID(),
      map: null,
      vectorLayer: null,
      markerLayer: null,
      extLayer: null,
      waterFactoryLayer: null,
      // 按状态分组的图层
      statusLayers: {
        '正常': null,
        '高压': null,
        '低压': null,
        '采集异常': null
      },
      // 状态可见性控制
      statusVisibility: {
        '正常': true,     // 默认显示正常状态
        '高压': true,     // 默认显示高压状态
        '低压': true,     // 默认显示低压状态
        '采集异常': true  // 默认显示采集异常状态
      },
      // 水厂图层可见性控制
      waterFactoryVisibility: true,
      // 状态数量统计
      statusCounts: {
        '正常': 0,
        '高压': 0,
        '低压': 0,
        '采集异常': 0
      },
      // 标记点信息面板
      markerInfo: null,
      showInfoPanel: false,
      // popup相关
      popupOverlay: null,
      popupElement: null,
      // 存储每个标记点的popup overlay
      markerPopups: null
    }
  },
  mounted () {
    // 初始化markerPopups为普通对象
    this.markerPopups = {};
    console.log('markerPopups已初始化:', this.markerPopups);

    this.initMap();
    // 并行加载数据，避免延迟
    Promise.all([
      this.getMarkerData(),
      this.getExtLayerData(),
      this.getWaterFactoryData()
    ]).then(() => {
      console.log('所有数据加载完成');
    }).catch(error => {
      console.error('数据加载失败:', error);
    });
  },
  methods: {
    initMap () {
      try {
        console.log('开始初始化地图...');
        // 检查DOM元素是否存在
        const targetElement = document.getElementById(this.mapId);
        if (!targetElement) {
          console.error('地图容器元素不存在，ID:', this.mapId);
          return;
        }
        console.log('地图容器元素找到:', targetElement);

        const center = this.getInitialCenter();
        console.log('地图中心点:', center);

        this.map = new Map({
          controls: [],
          interactions: defaultInteractions({
            doubleClickZoom: false // 禁用双击放大
          }),
          view: new View({
            center: center,
            projection: 'EPSG:4326',
            zoom: 12,
            maxZoom: 28,
            minZoom: 1,
            constrainResolution: true, // 限制分辨率为整数缩放级别，提升性能
            smoothResolutionConstraint: false // 禁用平滑分辨率约束，加快缩放
          }),
          target: this.mapId,
          layers: [],
          loadTilesWhileAnimating: true, // 动画时继续加载瓦片
          loadTilesWhileInteracting: true // 交互时继续加载瓦片
        });
        console.log('地图实例创建成功:', this.map);
        this.createBaseLayer();
        this.createPolygonLayer();
        this.createWaterFactoryLayer();
        this.createMarkerLayer();
        this.createPopupOverlay();
        this.addMarkerClickEvents();

        // 动态添加比例尺控件
        this.$nextTick(() => {
          const scaleLineControl = new ScaleLine({
            units: 'metric',
            target: 'scalebar',
            className: 'ol-scale-line'
          });
          this.map.addControl(scaleLineControl);
          console.log('比例尺控件已添加');
        });

        // 添加缩放级别监听器
        this.addZoomLevelListener();

        console.log('地图初始化完成');
      } catch (error) {
        console.error('地图初始化失败:', error);
      }
    },
    // 中心定位
    getInitialCenter () {
      return storage.get(LonLat);
    },
    // 加载底图
    createBaseLayer () {
      console.log('创建底图...');

      // 使用高德地图默认URL
      const url = 'https://wprd0{1-4}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&style=7&x={x}&y={y}&z={z}';

      // 检查是否已经注册过GCJ-02投影
      let gcjMecator = olProj.get('GCJ-02');

      if (!gcjMecator) {
        // 如果没有注册过，则创建并注册
        const gcj02Extent = [-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244]
        gcjMecator = new olProj.Projection({ code: 'GCJ-02', extent: gcj02Extent, units: 'm' })
        olProj.addProjection(gcjMecator)
        olProj.addCoordinateTransforms('EPSG:4326', gcjMecator, window.projzh.ll2gmerc, window.projzh.gmerc2ll)
        olProj.addCoordinateTransforms('EPSG:3857', gcjMecator, window.projzh.smerc2gmerc, window.projzh.gmerc2smerc)
        console.log('GCJ-02投影已注册');
      } else {
        console.log('GCJ-02投影已存在，直接使用');
      }

      this.baseLayer = new TileLayer({
        properties: {
          name: '底图',
          title: '地图图层',
          isBaseLayer: true
        },
        preload: 1, // 预加载1级瓦片
        source: new XYZ({
          projection: gcjMecator,
          crossOrigin: 'anonymous',
          url: url,
          maxZoom: 18,
          wrapX: true, // 启用水平环绕
          cacheSize: 2048, // 大幅增加缓存大小，从256提升到2048
          transition: 0, // 禁用瓦片加载过渡动画，加快渲染
          interpolate: false, // 禁用图像平滑处理，加快渲染
          tileSize: 256,
          tilePixelRatio: 1, // 使用标准像素比，不使用高清瓦片
          reprojectionErrorThreshold: 0.5 // 重投影误差阈值，降低精度要求以提升速度
        }),
        useInterimTilesOnError: false, // 瓦片加载错误时不使用临时瓦片
        renderMode: 'image' // 使用image模式渲染，比vector快
      });

      this.map.getLayers().insertAt(0, this.baseLayer);
      console.log('底图创建完成');
    },
    // 获取点位数据
    getMarkerData () {
      return this.$http.get(this.markerUrl).then(res => {
        const responseData = res.data;

        // 更新状态数量统计
        this.statusCounts = {
          '正常': responseData.normalCount || 0,
          '高压': responseData.highPressureCount || 0,
          '低压': responseData.lowPressureCount || 0,
          '采集异常': responseData.abnormalCount || 0
        };

        // 保存items数据
        this.markerData = responseData.items || [];

        // 数据获取后，生成标记点
        this.addMarkersToMap();
        console.log('标记点数据加载完成，状态数量:', this.statusCounts);
      }).catch(error => {
        console.error('标记点数据加载失败:', error);
        throw error;
      });
    },

    // 获取扩展图层数据
    getExtLayerData () {
      return this.$http.get(this.extLayerUrl + '/WATER_SUPPLY_RANGE').then(res => {
        // 确保extLayerData是数组格式
        this.extLayerData = Array.isArray(res.data) ? res.data : [res.data];
        console.log('扩展图层数据:', this.extLayerData);
        // 数据获取后，添加面到地图
        this.addPolygonToMap();
        console.log('面图层数据加载完成');
      }).catch(error => {
        console.error('面图层数据加载失败:', error);
        throw error;
      });
    },

    // 获取水厂数据
    getWaterFactoryData () {
      return this.$http.get(this.extLayerUrl + '/WATER_FACTORY_RANGE').then(res => {
        this.waterFactoryData = res.data;
        console.log('水厂数据:', this.waterFactoryData);
        // 数据获取后，添加水厂到地图
        this.addWaterFactoryToMap();
        console.log('水厂数据加载完成');
      }).catch(error => {
        console.error('水厂数据加载失败:', error);
        throw error;
      });
    },

    // 添加面到地图
    addPolygonToMap () {
      if (!this.polygonSource || !this.extLayerData || this.extLayerData.length === 0 || !this.extLayerData[0].geoJson) {
        console.log('面数据源或数据不存在');
        return;
      }

      try {
        console.log('开始添加面数据...');

        // 解析GeoJSON数据
        const geoJsonData = JSON.parse(this.extLayerData[0].geoJson);
        console.log('解析的GeoJSON数据:', geoJsonData);

        // 创建GeoJSON格式器
        const geoJsonFormat = new GeoJSON({
          dataProjection: 'EPSG:4326', // 源数据投影
          featureProjection: 'EPSG:4326' // 目标投影
        });

        // 读取要素
        const features = geoJsonFormat.readFeatures(geoJsonData);
        console.log('读取的要素数量:', features.length);

        // 为要素添加属性
        features.forEach(feature => {
          feature.setProperties({
            layerName: this.extLayerData.layerName,
            layerCode: this.extLayerData.layerCode,
            id: this.extLayerData.id
          });
        });

        // 批量添加要素，提升性能
        this.polygonSource.addFeatures(features);
        console.log('成功添加面要素到地图');

        // 预加载面数据到缓存
        this.polygonSource.forEachFeature(feature => {
          // 触发要素的几何体计算，预加载到缓存
          feature.getGeometry();
        });

        // 缩放到面范围
        if (features.length > 0) {
          const extent = this.polygonSource.getExtent();
          this.map.getView().fit(extent, {
            padding: [50, 50, 50, 50], // 边距
            duration: 1000 // 动画时长
          });
        }

      } catch (error) {
        console.error('添加面数据失败:', error);
      }
    },

    // 添加水厂面到地图
    addWaterFactoryToMap () {
      if (!this.waterFactorySource || !this.waterFactoryData || this.waterFactoryData.length === 0) {
        console.log('水厂数据源或数据不存在');
        return;
      }

      try {
        console.log('开始添加水厂面数据...');

        this.waterFactoryData.forEach((item, index) => {
          try {
            // 解析GeoJSON数据
            const geoJsonData = JSON.parse(item.geoJson);
            console.log('解析的水厂GeoJSON数据:', geoJsonData);

            // 创建GeoJSON格式器
            const geoJsonFormat = new GeoJSON({
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:4326'
            });

            // 读取要素
            const features = geoJsonFormat.readFeatures(geoJsonData);
            console.log('读取的水厂要素数量:', features.length);

            // 为要素添加属性
            features.forEach(feature => {
              feature.setProperties({
                layerName: item.layerName || '水厂',
                layerCode: item.layerCode || 'WATER_FACTORY_RANGE',
                id: item.id,
                type: '水厂',
                name: item.name || '未知水厂',
                address: item.address || '未知地址',
                description: item.description || '水厂'
              });
            });

            // 批量添加要素
            this.waterFactorySource.addFeatures(features);
          } catch (error) {
            console.error(`解析第 ${index + 1} 个水厂数据失败:`, error, item);
          }
        });

        console.log('成功添加水厂面要素到地图');

        // 为每个水厂面添加标签
        this.addWaterFactoryLabels();

      } catch (error) {
        console.error('添加水厂面数据失败:', error);
      }
    },

    // 为水厂面添加标签
    addWaterFactoryLabels () {
      if (!this.waterFactorySource) {
        console.log('水厂数据源不存在');
        return;
      }

      try {
        console.log('开始为水厂面添加标签...');

        // 获取所有水厂要素
        const features = this.waterFactorySource.getFeatures();

        features.forEach(feature => {
          const properties = feature.getProperties();
          const geometry = feature.getGeometry();

          // 只对面要素添加标签，排除标签要素
          if (geometry && properties.layerName && properties.type === '水厂') {
            // 计算面的中心点
            const extent = geometry.getExtent();
            const centerX = (extent[0] + extent[2]) / 2;
            const centerY = (extent[1] + extent[3]) / 2;

            // 创建标签要素
            const labelFeature = new Feature({
              geometry: new Point([centerX, centerY]),
              type: 'waterFactoryLabel',
              labelText: properties.layerName,
              parentId: properties.id
            });

            // 添加到水厂数据源
            this.waterFactorySource.addFeature(labelFeature);
          }
        });

        console.log('水厂面标签添加完成');
      } catch (error) {
        console.error('添加水厂面标签失败:', error);
      }
    },

    // 创建popup overlay
    createPopupOverlay () {
      // 创建popup元素
      this.popupElement = document.createElement('div');
      this.popupElement.className = 'marker-popup';
      this.popupElement.style.cssText = `
        position: absolute;
        background: url('${require('@/assets/pressure/box.png')}') no-repeat center center;
        background-size: 100% 100%;
        border: none;
        border-radius: 8px;
        padding: 12px 16px;
        font-size: 12px;
        box-shadow: 0 4px 15px rgba(0,0,0,0.2), 0 2px 8px rgba(0,0,0,0.1);
        pointer-events: none;
        z-index: 1000;
        width: 167px;
        height: 75px;
        white-space: normal;
        word-wrap: break-word;
        line-height: 1.4;
        text-align: left;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        color: white;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
      `;

      // 创建overlay
      this.popupOverlay = new Overlay({
        element: this.popupElement,
        positioning: 'center-left',
        stopEvent: false,
        offset: [0, -25]
      });

      // 添加到地图
      this.map.addOverlay(this.popupOverlay);
      console.log('Popup overlay创建成功');
    },

    // 创建标记点图层 - 按状态分组创建独立图层
    createMarkerLayer () {
      console.log('创建WebGL独立图标标记点图层...');

      // 创建数据源
      this.markerSource = new VectorSource();

      // 为每个状态创建独立的图层
      const statuses = ['正常', '高压', '低压', '采集异常'];
      const statusIcons = {
        '正常': require('@/assets/pressure/marker-normal.png'),
        '高压': require('@/assets/pressure/marker-high.png'),
        '低压': require('@/assets/pressure/marker-low.png'),
        '采集异常': require('@/assets/pressure/marker-error.png')
      };

      statuses.forEach(status => {
        // 创建状态数据源
        this[`${status}Source`] = new VectorSource();

        // 创建状态图层
        this.statusLayers[status] = new WebGLPointsLayer({
          source: this[`${status}Source`],
          style: {
            'symbol': {
              'symbolType': 'image',
              'src': statusIcons[status], // 使用独立图标
              'size': [24, 24], // 更小的图标尺寸
              'opacity': 1, // 始终显示，通过图层可见性控制
              'rotation': 0,
              'rotateWithView': false,
            }
          },
          disableHitDetection: false, // 启用点击检测
          visible: this.statusVisibility[status] // 设置初始可见性
        });

        // 添加到地图
        this.map.addLayer(this.statusLayers[status]);
      });

      console.log('WebGL独立图标标记点图层创建完成');
    },

    // 创建面图层
    createPolygonLayer () {
      console.log('创建面图层...');

      // 创建面数据源，启用缓存
      this.polygonSource = new VectorSource({
        useSpatialIndex: true, // 启用空间索引，提升性能
        wrapX: false // 禁用X轴环绕
      });

      // 创建面图层
      this.extLayer = new VectorLayer({
        source: this.polygonSource,
        style: new Style({
          fill: new Fill({
            color: 'rgba(77, 246, 230, 0.15)' // 更透明的青绿色填充
          }),
          stroke: new Stroke({
            color: '#4df6e6', // 青绿色边框
            width: 2
          })
        }),
        renderMode: 'image', // 使用image渲染模式，提升性能
        updateWhileAnimating: true, // 动画时更新
        updateWhileInteracting: true // 交互时更新
      });

      // 将面图层插入到底图之后，标记点之前
      this.map.getLayers().insertAt(1, this.extLayer);
      console.log('面图层创建完成');
    },

    // 创建水厂面图层
    createWaterFactoryLayer () {
      console.log('创建水厂面图层...');

      // 创建水厂数据源
      this.waterFactorySource = new VectorSource({
        useSpatialIndex: true,
        wrapX: false
      });

      // 创建水厂面图层
      this.waterFactoryLayer = new VectorLayer({
        source: this.waterFactorySource,
        style: (feature) => {
          const properties = feature.getProperties();

          // 如果是标签要素，返回文字样式
          if (properties.type === 'waterFactoryLabel') {
            return new Style({
              text: new Text({
                text: properties.labelText,
                font: '14px Arial, sans-serif',
                fill: new Fill({
                  color: '#333333'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                }),
                offsetY: 0,
                textAlign: 'center',
                textBaseline: 'middle'
              })
            });
          }

          // 如果是面要素，返回面样式
          return new Style({
            fill: new Fill({
              color: 'rgba(128, 128, 128, 0.3)' // 灰色半透明填充
            }),
            stroke: new Stroke({
              color: '#808080', // 灰色边框
              width: 2
            })
          });
        },
        renderMode: 'image',
        updateWhileAnimating: true,
        updateWhileInteracting: true,
        visible: this.waterFactoryVisibility
      });

      // 将水厂图层插入到面图层之后，标记点之前
      this.map.getLayers().insertAt(2, this.waterFactoryLayer);
      console.log('水厂面图层创建完成');
    },
    // 添加标记点到地图 - 按状态分组
    addMarkersToMap () {
      if (!this.markerData || this.markerData.length === 0) {
        console.log('标记点数据不存在');
        return;
      }

      console.log('开始添加标记点，数据量:', this.markerData.length);

      // 按状态分组要素
      const statusFeatures = {
        '正常': [],
        '高压': [],
        '低压': [],
        '采集异常': []
      };

      this.markerData.forEach((item, index) => {
        try {
          // 解析geoJson数据
          const geoJsonData = JSON.parse(item.geoJson);
          const coordinates = geoJsonData.coordinates; // [经度, 纬度]

          // 创建点几何对象
          const point = new Point(coordinates);

          // 创建要素
          const feature = new Feature({
            geometry: point,
            status: item.status,
            address: item.address,
            piezometerNo: item.piezometerNo,
            piezometerValue: item.piezometerValue,
            collectTime: item.collectTime,
            groundElevation: item.groundElevation,
            abnormalValue: item.abnormalValue
          });

          // 按状态分组
          if (statusFeatures[item.status]) {
            statusFeatures[item.status].push(feature);
          } else {
            console.warn(`未知状态: ${item.status}`);
          }
        } catch (error) {
          console.error(`解析第 ${index + 1} 个标记点数据失败:`, error, item);
        }
      });

      // 将要素添加到对应的状态数据源
      Object.keys(statusFeatures).forEach(status => {
        if (this[`${status}Source`] && statusFeatures[status].length > 0) {
          this[`${status}Source`].addFeatures(statusFeatures[status]);
          console.log(`成功添加 ${statusFeatures[status].length} 个${status}标记点`);

          // 为非正常状态的标记点默认创建popup
          if (status !== '正常') {
            this.createPopupsForStatus(status, statusFeatures[status]);
          }
          // 正常状态的标记点不默认创建popup，只在点击时创建
        }
      });
    },

    // 为指定状态创建popup
    createPopupsForStatus (status, features) {
      if (!this.popupOverlay) {
        console.error('Popup overlay未初始化');
        return;
      }

      // 确保markerPopups已初始化
      if (!this.markerPopups) {
        console.log('markerPopups未初始化，重新初始化');
        this.markerPopups = {};
      }

      features.forEach(feature => {
        const geometry = feature.getGeometry();
        const coordinates = geometry.getCoordinates();
        const properties = feature.getProperties();

        // 创建唯一标识符
        const markerId = `${coordinates[0]}_${coordinates[1]}_${status}`;

        // 检查是否已存在popup，如果存在则跳过
        if (this.markerPopups && this.markerPopups[markerId]) {
          console.log(`标记点 ${markerId} 已存在popup，跳过创建`);
          return;
        }

        // 使用通用方法创建popup
        this.createSinglePopup(properties, status, coordinates, markerId);
      });

      console.log(`为${status}状态创建了${features.length}个popup`);
    },

    // 通用popup创建方法
    createSinglePopup (properties, status, coordinates, markerId) {
      console.log(properties);

      // 获取状态对应的背景图片
      const getStatusBackground = (status) => {
        const backgrounds = {
          '正常': require('@/assets/pressure/box4.png'),
          '低压': require('@/assets/pressure/box1.png'),
          '高压': require('@/assets/pressure/box2.png'),
          '采集异常': require('@/assets/pressure/box3.png')
        };
        return backgrounds[status] || require('@/assets/pressure/box.png');
      };

      const statusBackground = getStatusBackground(status);

      // 创建popup内容
      const popupContent = `
        <div class="popup-content" style="display: flex; flex-direction: column; justify-content: space-between; align-items: center; height: 100%; text-align: center; padding: 8px 12px;">
          <div class="popup-address" style="font-size: 12px; font-weight: bold; line-height: 1.2; color: #f0f0f0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px; text-shadow: 0 1px 2px rgba(0,0,0,0.4); min-height: 12px; margin-top: 2px;">
            ${properties.address || '未知地址'}
          </div>
          <div class="popup-value" style="font-size: 14px; font-weight: bold; color: #ffffff; text-shadow: 0 1px 3px rgba(0,0,0,0.5); text-align: center; min-height: 18px; display: flex; align-items: center; justify-content: center;">
            ${properties.piezometerValue === null ? '采集异常' : (properties.piezometerValue + ' Mpa' || '采集异常')}
          </div>
          <div class="popup-time" style="font-size: 10px; color: #e0e0e0; text-shadow: 0 1px 2px rgba(0,0,0,0.4); min-height: 12px; display: flex; align-items: center; justify-content: center;">
            ${properties.collectTime && properties.collectTime !== '未知时间' ? properties.collectTime : ''}
          </div>
        </div>
      `;

      // 创建popup容器
      const popupContainer = document.createElement('div');
      popupContainer.style.cssText = `
        position: relative;
        width: 167px;
        height: 75px;
      `;

      // 创建popup元素
      const popupElement = document.createElement('div');
      popupElement.className = 'marker-popup';
      popupElement.innerHTML = popupContent;
      popupElement.style.cssText = `
        position: absolute;
        background: url('${statusBackground}') no-repeat center center;
        background-size: 100% 100%;
        border: none;
        border-radius: 8px;
        font-size: 12px;
        pointer-events: none;
        z-index: 1000;
        width: 167px;
        height: 75px;
        white-space: normal;
        word-wrap: break-word;
        line-height: 1.4;
        text-align: left;
        transform: none !important;
        left: auto !important;
        top: auto !important;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        color: white;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
      `;

      // 创建按钮元素
      const buttonElement = document.createElement('button');
      buttonElement.className = 'popup-detail-btn';
      buttonElement.innerHTML = '';
      buttonElement.style.cssText = `
        position: absolute;
        right: 10%;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 12px;
        background: url('${require('@/assets/pressure/button.png')}') no-repeat center center;
        background-size: 100% 100%;
        border: none;
        cursor: pointer;
        transition: all 0.2s;
        z-index: 1001;
        display: flex;
        align-items: center;
        justify-content: center;
        filter: brightness(0) invert(1);
      `;

      // 将popup和按钮添加到容器
      popupContainer.appendChild(popupElement);
      popupContainer.appendChild(buttonElement);

      // 为详情按钮添加点击事件
      buttonElement.style.pointerEvents = 'auto'; // 允许按钮接收点击事件
      buttonElement.addEventListener('click', (e) => {
        e.stopPropagation(); // 阻止事件冒泡
        this.showMarkerInfoPanel(properties, status);
      });

      // 添加悬停效果
      buttonElement.addEventListener('mouseover', () => {
        buttonElement.style.filter = 'brightness(1) invert(0)';
        buttonElement.style.transform = 'translateY(-50%) scale(1.1)';
      });
      buttonElement.addEventListener('mouseout', () => {
        buttonElement.style.filter = 'brightness(0) invert(1)';
        buttonElement.style.transform = 'translateY(-50%) scale(1)';
      });

      // 创建overlay
      const popupOverlay = new Overlay({
        element: popupContainer,
        positioning: 'center-left',
        stopEvent: false,
        offset: [-85, -50]
      });

      // 设置位置
      popupOverlay.setPosition(coordinates);

      // 添加到地图
      this.map.addOverlay(popupOverlay);

      // 存储到对象中
      if (this.markerPopups) {
        this.markerPopups[markerId] = popupOverlay;
      }

    },

    // 获取状态颜色
    getStatusColor (status) {
      const colors = {
        '高压': '#ff4d4f',
        '低压': '#1890ff',
        '采集异常': '#fa8c16'
      };
      return colors[status] || '#8c8c8c';
    },

    // 获取状态可见性
    getStatusVisibility (status) {
      return this.statusVisibility[status] ? 1 : 0;
    },

    // 切换状态可见性
    toggleStatusVisibility (status) {
      this.statusVisibility[status] = !this.statusVisibility[status];
      this.updateMarkerLayerStyle();
      console.log(`状态 ${status} 的可见性已设置为: ${this.statusVisibility[status]}`);
    },

    // 切换供水范围图层显示隐藏
    toggleExtLayerVisibility (visible) {
      if (this.extLayer) {
        this.extLayer.setVisible(visible);
        console.log(`供水范围图层可见性已设置为: ${visible}`);
      }
    },

    // 切换水厂图层显示隐藏
    toggleWaterFactoryVisibility (visible) {
      if (this.waterFactoryLayer) {
        this.waterFactoryLayer.setVisible(visible);
        this.waterFactoryVisibility = visible;
        console.log(`水厂图层可见性已设置为: ${visible}`);
      }
    },

    // 显示所有状态
    showAllStatus () {
      Object.keys(this.statusVisibility).forEach(status => {
        this.statusVisibility[status] = true;
      });
      this.updateMarkerLayerStyle();
      console.log('所有状态已显示');
    },

    // 隐藏所有状态
    hideAllStatus () {
      Object.keys(this.statusVisibility).forEach(status => {
        this.statusVisibility[status] = false;
      });
      this.updateMarkerLayerStyle();
      console.log('所有状态已隐藏');
    },

    // 更新标记点图层样式 - 使用图层可见性控制
    updateMarkerLayerStyle () {
      // 直接控制每个状态图层的可见性
      Object.keys(this.statusLayers).forEach(status => {
        if (this.statusLayers[status]) {
          this.statusLayers[status].setVisible(this.statusVisibility[status]);

          // 如果图层被隐藏，清除对应的popup
          if (!this.statusVisibility[status]) {
            this.clearPopupsByStatus(status);
          }
        }
      });
      console.log('标记点图层可见性已更新');
    },

    // 添加标记点点击事件
    addMarkerClickEvents () {
      // 使用地图点击事件来检测标记点点击
      this.map.on('click', (event) => {
        this.handleMapClick(event);
      });
      console.log('地图点击事件已添加');
    },

    // 处理地图点击事件
    handleMapClick (event) {
      console.log('地图被点击:', event);

      // 检查是否点击了标记点
      const features = this.map.getFeaturesAtPixel(event.pixel);

      if (features && features.length > 0) {
        // 找到标记点要素
        const markerFeature = features.find(feature => {
          const properties = feature.getProperties();
          return properties.status && ['正常', '高压', '低压', '采集异常'].includes(properties.status);
        });

        if (markerFeature) {
          const properties = markerFeature.getProperties();
          const status = properties.status;
          const coordinates = markerFeature.getGeometry().getCoordinates();
          console.log('点击了标记点:', properties);
          this.showMarkerInfo(properties, status, coordinates);
          return;
        }

        // 找到水厂要素（排除标签）
        const waterFactoryFeature = features.find(feature => {
          const properties = feature.getProperties();
          return properties.type === '水厂' && properties.type !== 'waterFactoryLabel';
        });

        if (waterFactoryFeature) {
          const properties = waterFactoryFeature.getProperties();
          console.log('点击了水厂:', properties);
          this.showWaterFactoryInfo(properties);
          return;
        }

        // 点击了其他要素，不隐藏popup
        console.log('点击了其他要素，保持popup显示');
      } else {
        // 点击了空白区域，不隐藏popup
        console.log('点击了空白区域，保持popup显示');
      }
    },


    // 显示标记点信息
    showMarkerInfo (properties, status, coordinates = null) {
      console.log('showMarkerInfo被调用:', properties, status);

      // 获取标记点坐标
      let markerCoordinates = coordinates;
      if (!markerCoordinates && properties.geometry) {
        markerCoordinates = properties.geometry.getCoordinates();
      }

      if (!markerCoordinates) {
        console.error('无法获取标记点坐标');
        return;
      }

      // 创建唯一标识符
      const markerId = `${markerCoordinates[0]}_${markerCoordinates[1]}_${status}`;

      // 切换popup显示状态
      this.toggleMarkerPopup(properties, status, coordinates, markerId);
    },

    // 切换标记点popup显示状态
    toggleMarkerPopup (properties, status, coordinates, markerId) {
      // 检查是否已存在popup
      if (this.markerPopups && this.markerPopups[markerId]) {
        console.log(`标记点 ${markerId} 已存在popup，移除它`);
        this.removeMarkerPopup(markerId);
        return;
      }

      // 创建新的popup
      console.log(`标记点 ${markerId} 不存在popup，创建新的`);
      this.createPopupForMarker(properties, status, coordinates);
    },

    // 为标记点创建popup
    createPopupForMarker (properties, status, coordinates = null) {
      if (!this.map) {
        console.error('地图未初始化');
        return;
      }

      // 确保markerPopups已初始化
      if (!this.markerPopups) {
        console.log('markerPopups未初始化，重新初始化');
        this.markerPopups = {};
      }

      // 获取标记点坐标
      let markerCoordinates = coordinates;
      if (!markerCoordinates && properties.geometry) {
        markerCoordinates = properties.geometry.getCoordinates();
      }

      if (!markerCoordinates) {
        console.error('无法获取标记点坐标');
        return;
      }

      // 创建唯一标识符
      const markerId = `${markerCoordinates[0]}_${markerCoordinates[1]}_${status}`;

      // 使用通用方法创建popup
      this.createSinglePopup(properties, status, markerCoordinates, markerId);
    },

    // 移除标记点的popup
    removeMarkerPopup (markerId) {
      if (this.markerPopups && this.markerPopups[markerId]) {
        const popupOverlay = this.markerPopups[markerId];
        this.map.removeOverlay(popupOverlay);
        delete this.markerPopups[markerId];
        console.log(`移除了标记点 ${markerId} 的popup`);
      }
    },

    // 清理所有popup
    clearAllPopups () {
      if (this.markerPopups) {
        Object.keys(this.markerPopups).forEach(markerId => {
          const popupOverlay = this.markerPopups[markerId];
          this.map.removeOverlay(popupOverlay);
        });
        this.markerPopups = {};
        console.log('清理了所有popup');
      }
    },

    // 清理指定状态的popup
    clearPopupsByStatus (status) {
      if (this.markerPopups) {
        const popupsToRemove = [];

        // 找到所有属于该状态的popup
        Object.keys(this.markerPopups).forEach(markerId => {
          if (markerId.endsWith(`_${status}`)) {
            popupsToRemove.push(markerId);
          }
        });

        // 移除这些popup
        popupsToRemove.forEach(markerId => {
          const popupOverlay = this.markerPopups[markerId];
          this.map.removeOverlay(popupOverlay);
          delete this.markerPopups[markerId];
        });

        console.log(`清理了${status}状态的${popupsToRemove.length}个popup`);
      }
    },

    // 显示标记点信息面板
    showMarkerInfoPanel (properties, status) {
      console.log('显示标记点信息面板:', properties, status);

      // 设置标记点信息
      this.markerInfo = {
        status: status,
        address: properties.address || '未知地址',
        piezometerNo: properties.piezometerNo || '未知编号',
        piezometerValue: properties.piezometerValue === null ? '采集异常' : (properties.piezometerValue + ' Mpa' || '采集异常'),
        collectTime: properties.collectTime && properties.collectTime !== '未知时间' ? properties.collectTime : '未知时间',
        groundElevation: properties.groundElevation || '未知',
        abnormalValue: properties.abnormalValue || '无'
      };

      // 显示面板
      this.showInfoPanel = true;
    },

    // 关闭信息面板
    closeInfoPanel () {
      this.showInfoPanel = false;
      this.markerInfo = null;
    },

    // 显示水厂信息
    showWaterFactoryInfo (properties) {
      console.log('显示水厂信息:', properties);

      // 设置水厂信息
      this.markerInfo = {
        status: '水厂',
        address: properties.address || '未知地址',
        name: properties.name || '未知水厂',
        id: properties.id || '未知ID',
        description: properties.description || '水厂',
        type: '水厂'
      };

      // 显示面板
      this.showInfoPanel = true;
    },

    // 添加缩放级别监听器
    addZoomLevelListener () {
      this.map.getView().on('change:resolution', () => {
        const zoom = this.map.getView().getZoom();
        console.log('当前缩放级别:', zoom);

        if (zoom >= 15) {
          // 缩放级别达到15时，显示所有popup包括正常状态的
          this.showAllPopups();
        } else {
          // 缩放级别小于15时，只显示非正常状态的popup
          this.hideNormalPopups();
        }
      });
    },

    // 显示所有popup（包括正常状态）——须与图例开关一致：图例关闭某状态后缩放地图不应再把它显示出来
    showAllPopups () {
      console.log('显示所有popup');

      if (!this.statusVisibility['正常']) {
        this.hideNormalPopups();
        return;
      }

      if (this.正常Source) {
        const normalFeatures = this.正常Source.getFeatures();
        this.createPopupsForStatus('正常', normalFeatures);
      }
    },

    // 隐藏正常状态的popup
    hideNormalPopups () {
      console.log('隐藏正常状态popup');

      if (this.markerPopups) {
        const popupsToRemove = [];

        // 找到所有正常状态的popup
        Object.keys(this.markerPopups).forEach(markerId => {
          if (markerId.endsWith('_正常')) {
            popupsToRemove.push(markerId);
          }
        });

        // 移除这些popup
        popupsToRemove.forEach(markerId => {
          const popupOverlay = this.markerPopups[markerId];
          this.map.removeOverlay(popupOverlay);
          delete this.markerPopups[markerId];
        });

        console.log(`隐藏了${popupsToRemove.length}个正常状态popup`);
      }
    }

  }
}
</script>

<style lang="less" scoped>
.map-container {
  width: 100%;
  height: 90vh;
  position: relative;
}

.map-content {
  width: 100%;
  height: 100%;
  position: relative;
}

/* 比例尺容器 */
.scalebar-container {
  position: absolute;
  left: 10px;
  bottom: 10px;
  z-index: 1000;
  user-select: none;
}

/* 比例尺样式 */
/deep/.ol-scale-line {
  position: relative !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 4px !important;
  padding: 8px 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

/deep/ .ol-scale-line-inner {
  font-size: 12px !important;
  color: #333 !important;
  font-weight: 500 !important;
  border: 1px solid #333 !important;
  border-top: none !important;
  text-align: center !important;
}
</style>